import React, { useState } from "react";
import { UrlObject } from "url";
import Link from "next/link";
import { Routes } from "generated/routes";
import config from "config";
import { isBrowser } from "lib/env";
import { sendGAEvent } from "lib/analytics";

import styles from "./EventRightContent.module.scss";
import { Button } from "../../components/atoms/button";
import { TableOfContents } from "../../components/molecules/tableOfContents";
import {
  CancelEventForm,
  CancelEventRegistrationModal,
} from "../../components/molecules/cancelEventRegistration";
import { ContentHeadlineItem, CurrentEntor } from "../../types";
import { EventRegistrationStatus } from "../../common";
import { EventType } from "../../generated/graphql";
import { ConfirmModal } from "../../components/molecules/confirmModal";

interface Props {
  status: EventRegistrationStatus | "dateNotSelected";
  registrationButtonURL?: UrlObject;
  activeHeadline: number;
  goToHeadline: (id: number) => void;
  goToJobhuntInfoEdit: () => void;
  headlines: Array<ContentHeadlineItem>;
  cancelEvent: (form: CancelEventForm) => Promise<void>;
  eventType: EventType;
  eventIsFull: boolean;
  lotteryRejected: boolean;
  lotteryEvent: boolean;
  isInterviewSignupEvent?: boolean;
  canApplyForInt?: boolean;
  currentEntor?: CurrentEntor;
  baklavaDisableLegacyEventWrites?: boolean;
}

const EventRightContent: React.NamedExoticComponent<Props> = React.memo(
  ({
    activeHeadline,
    cancelEvent,
    status,
    goToHeadline,
    goToJobhuntInfoEdit,
    headlines,
    currentEntor,
    registrationButtonURL,
    eventType,
    eventIsFull,
    lotteryRejected,
    lotteryEvent,
    isInterviewSignupEvent = false,
    canApplyForInt = false,
    baklavaDisableLegacyEventWrites,
  }) => {
    const [cancelModalIsOpen, setIsOpenCancelModal] = useState(false);
    const [goToJobhuntModalIsOpen, setGoToJobhuntModalIsOpen] = useState(false);

    const buttonText = baklavaDisableLegacyEventWrites
      ? "受付を一時停止中です"
      : lotteryRejected
      ? "落選により申込不可"
      : status === "registered"
      ? "キャンセルする"
      : status === "outdated"
      ? "募集終了"
      : eventIsFull
      ? "満枠のため募集終了"
      : status === "notTargetEvent"
      ? "卒業年度対象外"
      : status === "dateNotSelected"
      ? "日程を選択して予約"
      : status === "registeredOtherDate"
      ? "他の日程を予約済み"
      : lotteryEvent
      ? "イベント(抽選あり)に申し込む"
      : eventType === EventType.Entry
      ? "イベント申し込み"
      : isInterviewSignupEvent
      ? "面談申し込みはこちら"
      : "申し込む";

    const registrationButton = () => {
      if (baklavaDisableLegacyEventWrites) {
        return (
          <Button disabled fluid>
            {buttonText}
          </Button>
        );
      }

      if (isInterviewSignupEvent) {
        return canApplyForInt ? (
          <Link href={Routes.url("/interview/signup", {})}>
            <Button fluid>{buttonText}</Button>
          </Link>
        ) : (
          <Button disabled fluid>
            {buttonText}
          </Button>
        );
      }

      if (lotteryRejected) {
        return (
          <Button fluid disabled>
            {buttonText}
          </Button>
        );
      }

      if (status === "registered") {
        return (
          <>
            <Button
              onClick={() => setIsOpenCancelModal(true)}
              color="danger"
              fluid
            >
              {buttonText}
            </Button>
            <CancelEventRegistrationModal
              cancelEvent={cancelEvent}
              isOpen={cancelModalIsOpen}
              setIsOpen={setIsOpenCancelModal}
            />
          </>
        );
      }

      if (status === "jobhuntInfoMissing") {
        return (
          <>
            <Button onClick={() => setGoToJobhuntModalIsOpen(true)} fluid>
              {buttonText}
            </Button>
            <ConfirmModal
              textDescription="このイベントに申し込むには、就活情報の更新が必要です。 就活情報を更新しますか?"
              cancelLabel={"戻る"}
              confirmLabel={"更新する"}
              onConfirm={goToJobhuntInfoEdit}
              isOpen={goToJobhuntModalIsOpen}
              onCloseRequest={() => setGoToJobhuntModalIsOpen(false)}
            />
          </>
        );
      }

      if (status !== "ok" || eventIsFull) {
        return (
          <Button disabled fluid>
            {buttonText}
          </Button>
        );
      }

      return registrationButtonURL ? (
        <Link href={getRegistrationURL(registrationButtonURL, currentEntor)}>
          <a
            onClick={() => {
              if (isBrowser)
                sendGAEvent({
                  event: "enc.event_application_start",
                  screen_name: "event_detail",
                });
            }}
          >
            <Button id="event-detail-apply" fluid>
              {buttonText}
            </Button>{" "}
          </a>
        </Link>
      ) : null;
    };

    return (
      <div className={styles.main}>
        <div className={styles.button}>{registrationButton()}</div>
        <div className={styles.toc}>
          <TableOfContents
            active={activeHeadline}
            onClick={goToHeadline}
            headlines={headlines}
          />
        </div>
      </div>
    );
  },
);

function getRegistrationURL(
  registrationButtonURL: UrlObject,
  currentEntor?: CurrentEntor,
): string | UrlObject {
  if (!currentEntor) {
    let returnTo = `${config.FRONT_END_URL}${registrationButtonURL.pathname}`;
    // @ts-ignore
    if (!!registrationButtonURL?.query?.venue) {
      // @ts-ignore
      returnTo += `?venue=${registrationButtonURL.query.venue}`;
    }
    return `${config.BAKLAVA_URL}/oidc/login?return_to=${returnTo}`;
  } else {
    return registrationButtonURL;
  }
}

export default EventRightContent;
